import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { FaCloudUploadAlt, FaCheckCircle, FaTimesCircle, FaSpinner, FaCamera, FaRedo, FaTimes } from 'react-icons/fa';
import { useAuth } from '../myTrack/AuthContext';

const ACCEPT = 'application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt';
const MAX_CONCURRENT = 2;

function uploadErrorMessage(err) {
    const detail = err.response?.data?.detail?.error?.message || err.response?.data?.detail?.error?.errors?.[0]?.reason;
    return detail ? `${err.response?.data?.message}: ${detail}` : (err.response?.data?.message || err.message || 'Upload failed');
}

/**
 * Drag-and-drop / browse / camera uploader.
 *
 * uploadUrl        — POST endpoint receiving multipart `file`
 * getExtraFields   — returns extra form fields for each upload (e.g. document type, expiry)
 * onUploadComplete — called once a batch finishes and at least one file succeeded
 * hint             — helper text under the drop prompt
 */
export default function DriveUploadZone({ uploadUrl, getExtraFields, onUploadComplete, hint, disabled = false }) {
    const { token } = useAuth();
    const [dragging, setDragging] = useState(false);
    const [uploads, setUploads] = useState([]);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const nextId = useRef(0);

    const updateUpload = (id, patch) => setUploads(prev => prev.map(u => (u.id === id ? { ...u, ...patch } : u)));

    const uploadOne = useCallback(async (item) => {
        const body = new FormData();
        body.append('file', item.file);
        Object.entries(item.fields || {}).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') body.append(key, value);
        });
        updateUpload(item.id, { status: 'uploading', progress: 0, error: null });
        try {
            await axios.post(uploadUrl, body, {
                headers: { Authorization: `Bearer ${token}` },
                onUploadProgress: (e) => {
                    const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
                    updateUpload(item.id, { progress: pct });
                },
            });
            updateUpload(item.id, { status: 'done', progress: 100 });
            return true;
        } catch (err) {
            updateUpload(item.id, { status: 'error', error: uploadErrorMessage(err) });
            return false;
        }
    }, [uploadUrl, token]);

    // Uploads a few at a time so a batch of phone photos doesn't saturate a mobile connection.
    const runQueue = useCallback(async (items) => {
        const queue = [...items];
        let succeeded = 0;
        const worker = async () => {
            while (queue.length) {
                const item = queue.shift();
                if (await uploadOne(item)) succeeded += 1;
            }
        };
        await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT, items.length) }, worker));

        if (succeeded > 0) onUploadComplete?.();
        const finishedIds = new Set(items.map(i => i.id));
        setTimeout(() => setUploads(prev => prev.filter(u => !(finishedIds.has(u.id) && u.status === 'done'))), 3500);
    }, [uploadOne, onUploadComplete]);

    const startUploads = useCallback((fileList) => {
        const fields = getExtraFields?.() || {};
        const items = Array.from(fileList).map(file => ({
            id: nextId.current++, file, fields, name: file.name, progress: 0, status: 'queued', error: null,
        }));
        setUploads(prev => [...prev.filter(u => u.status !== 'done'), ...items]);
        runQueue(items);
    }, [getExtraFields, runQueue]);

    const retry = (item) => runQueue([item]);
    const dismiss = (id) => setUploads(prev => prev.filter(u => u.id !== id));

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragging(false);
        if (!disabled && e.dataTransfer.files?.length) startUploads(e.dataTransfer.files);
    }, [disabled, startUploads]);

    const handleInput = (e) => {
        if (e.target.files?.length) startUploads(e.target.files);
        e.target.value = '';
    };

    const busy = uploads.some(u => u.status === 'uploading' || u.status === 'queued');

    return (
        <div className="mb-5">
            <div
                role="button"
                tabIndex={0}
                aria-disabled={disabled}
                onClick={() => !disabled && fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === 'Enter' && !disabled && fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                className={`rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors select-none
                    ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                    ${dragging ? 'border-[#FF5733] bg-orange-50' : 'border-gray-200 hover:border-[#FF5733] hover:bg-orange-50/40'}`}
            >
                <FaCloudUploadAlt className={`mx-auto mb-2 text-3xl transition-colors ${dragging ? 'text-[#FF5733]' : 'text-gray-300'}`} />
                <p className="text-sm font-medium text-gray-500">
                    Drop files here or <span className="text-[#FF5733]">browse</span>
                    {busy && <span className="text-gray-400"> · uploading…</span>}
                </p>
                <p className="text-xs text-gray-400 mt-1">{hint || 'PDF, images, Word, Excel — up to 25 MB each'}</p>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
                    className="md:hidden mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 disabled:opacity-50"
                >
                    <FaCamera /> Take photo
                </button>
                <input ref={fileInputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={handleInput} />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleInput} />
            </div>

            {uploads.length > 0 && (
                <ul className="mt-3 space-y-2">
                    {uploads.map((u) => (
                        <li key={u.id} className="flex items-center gap-3">
                            {(u.status === 'uploading' || u.status === 'queued') && <FaSpinner className="shrink-0 text-[#FF5733] animate-spin" />}
                            {u.status === 'done' && <FaCheckCircle className="shrink-0 text-green-500" />}
                            {u.status === 'error' && <FaTimesCircle className="shrink-0 text-red-500" />}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-700 truncate">{u.name}</p>
                                {u.status === 'uploading' && (
                                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                                        <div className="h-full rounded-full bg-[#FF5733] transition-all duration-200" style={{ width: `${u.progress}%` }} />
                                    </div>
                                )}
                                {u.status === 'error' && <p className="text-xs text-red-500 mt-0.5">{u.error}</p>}
                            </div>
                            {u.status === 'uploading' && <span className="shrink-0 text-xs text-gray-400">{u.progress}%</span>}
                            {u.status === 'queued' && <span className="shrink-0 text-xs text-gray-400">Waiting</span>}
                            {u.status === 'error' && (
                                <div className="flex shrink-0 items-center gap-1">
                                    <button type="button" onClick={() => retry(u)} className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-600 hover:bg-orange-50 hover:text-[#FF5733]" title="Retry upload">
                                        <FaRedo /> Retry
                                    </button>
                                    <button type="button" onClick={() => dismiss(u.id)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" title="Dismiss" aria-label={`Dismiss ${u.name}`}>
                                        <FaTimes />
                                    </button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
